import React from 'react';
import { useGetLibraryOptionsQuery } from '../../reactWrapper/Redux/apiSlice';
import { LIBRARY, chain } from './library.config';

const selectClass =
  'h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60';

/**
 * One level of the chain. Fetches its options from the API, filtered by the
 * level above; disabled until that level has a value.
 */
function LevelSelect({ kind, parentKind, parentId, value, onChange, mode, id, disabled }) {
  const cfg = LIBRARY[kind];
  const needsParent = !!parentKind;
  const skip = needsParent && !parentId;
  const { data, isFetching } = useGetLibraryOptionsQuery({ kind, parentId: parentId || undefined }, { skip: skip || disabled });
  const options = data || [];

  const placeholder = disabled
    ? 'Not needed'
    : skip
    ? `Choose a ${LIBRARY[parentKind].label.toLowerCase()} first`
    : mode === 'filter'
    ? `All ${cfg.plural.toLowerCase()}`
    : `Select a ${cfg.label.toLowerCase()}`;

  return (
    <div className='flex flex-col gap-1.5'>
      <label htmlFor={id} className='text-xs font-semibold text-muted-foreground'>
        {cfg.label}
      </label>
      <select
        id={id}
        className={selectClass}
        disabled={skip || disabled}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      >
        <option value=''>{isFetching ? 'Loading…' : placeholder}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
            {o.status === 'pending' ? ' (pending)' : ''}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Dropdowns for the ancestors of `kind`. `value` is { category: id, role: id, ... }.
 *
 * mode="form"   used inside the upload / edit modal (placeholders say "Select a …")
 * mode="filter" used above the table (placeholders say "All …")
 * direct        show only the immediate parent, listing every one of them.
 *               The form uses this: a skill is tied to a role, and the role
 *               already knows its category, so asking for the category too
 *               is a second choice that decides nothing.
 * disabled      greys every level out (a generic skill has no parent)
 */
export default function ParentSelects({ kind, value, onChange, mode = 'form', idPrefix = 'parent', direct = false, disabled = false }) {
  const full = chain(kind);
  const levels = direct ? full.slice(-1) : full;
  if (!levels.length) return null;

  const set = (level, id) => {
    const next = { ...value, [level]: id };
    const idx = levels.indexOf(level);
    levels.slice(idx + 1).forEach((k) => {
      next[k] = null;
    });
    onChange(next);
  };

  /* With only the immediate parent on screen there is nothing above it to
     filter by, so it lists everything. */
  if (direct) {
    const level = levels[0];
    return (
      <LevelSelect
        id={`${idPrefix}-${level}`}
        kind={level}
        parentKind={null}
        parentId={null}
        value={value?.[level]}
        onChange={(id) => set(level, id)}
        mode={mode}
        disabled={disabled}
      />
    );
  }

  return (
    <>
      {levels.map((level, i) => {
        const parentKind = i > 0 ? levels[i - 1] : null;
        return (
          <LevelSelect
            key={level}
            id={`${idPrefix}-${level}`}
            kind={level}
            parentKind={parentKind}
            parentId={parentKind ? value?.[parentKind] : null}
            value={value?.[level]}
            onChange={(id) => set(level, id)}
            mode={mode}
            disabled={disabled}
          />
        );
      })}
    </>
  );
}
